import { Container, IconButton, Tooltip, Typography } from '@material-ui/core';
import Snackbar from '@material-ui/core/Snackbar';
import { makeStyles } from '@material-ui/core/styles';
import DoneIcon from '@material-ui/icons/Done';
import MuiAlert, { AlertProps } from '@material-ui/lab/Alert';
import { useQuery } from '@tanstack/react-query';
import { SetStateAction, useState } from 'react';
import { getErp } from '../../app/services/erp/get-erp.api';
import { approveQuotes } from '../../app/services/quotes/approve-quotes.api';
import { customtheme } from '../../app/theme/theme';
import EnhancedTable, { HeadCell } from '../../common/components/EnhancedTable';
import ApproveQuotesEmitDrawer from './ApproveQuotesEmitDrawer';
import { Info } from '@material-ui/icons';

const useStyles = makeStyles(() => ({
  root: { backgroundColor: customtheme.gray[200], display: 'flex', flexDirection: 'column', flex: 1 },
  tableSection: { padding: 24, flex: 1, display: 'flex' },
  tableSectionContainer: { padding: 0, flex: 1, display: 'flex', flexDirection: 'column', maxWidth: '100%' },
  drawer: { width: 500 },
  drawerHeader: { padding: 16, borderBottom: `1px solid #eee`, background: customtheme.gray[200], display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  drawerContent: { padding: 16, overflowY: 'auto', height: '100%' },
  drawerFooter: { padding: 16, borderTop: `1px solid #eee`, background: customtheme.gray[200], display: 'flex', justifyContent: 'flex-end' }
}));

const ApproveQuotesEmit = () => {
  const classes = useStyles();
  const [selectedData, setSelectedData] = useState<any[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  // const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [count, setCount] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerRecord, setDrawerRecord] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSnackbar, setIsSnackbar] = useState({ open: false, message: '', title: '' });

  const formatNumber = (value: any) => {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return value ?? '';
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(numericValue);
  };

  const formatCurrency = (value: any) => {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return value ?? '';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(numericValue);
  };

  const readFirstValue = (source: any, paths: string[]) => {
    for (const path of paths) {
      const value = source?.[path];
      if (value !== null && value !== undefined && value !== '') {
        return value;
      }
    }
    return '';
  };

  const normalizeToNumber = (value: any) => {
    const rawValue = String(value ?? '').trim();
    if (!rawValue) return null;

    const normalizedValue = rawValue.includes(',')
      ? rawValue.replace(/\./g, '').replace(',', '.')
      : rawValue;

    const numericValue = Number(normalizedValue);
    return Number.isNaN(numericValue) ? null : numericValue;
  };

  const toComparableNumber = (value: any) => {
    const numericValue = normalizeToNumber(value);
    return numericValue === null ? Number.POSITIVE_INFINITY : numericValue;
  };

  const headCells: HeadCell[] = [
    { id: 'numero', numeric: true, disablePadding: false, label: 'Número Cotação' },
    { id: 'numerosolicitacao', numeric: true, disablePadding: false, label: 'Número Solicitação' },
    { id: 'fornecedor', numeric: false, disablePadding: true, label: 'Fornecedor' },
    { id: 'produto', numeric: false, disablePadding: true, label: 'Produto' },
    { id: 'quantidade', numeric: false, disablePadding: true, label: 'Quantidade' },
    { id: 'precounitario', numeric: false, disablePadding: true, label: 'Preço Unitário' },
    { id: 'valorliquido', numeric: false, disablePadding: true, label: 'Valor Líquido' },
    { id: 'nomemp', numeric: false, disablePadding: true, label: 'Empresa' },
    { id: 'datcot', numeric: false, disablePadding: true, label: 'Data Cotação' },
    { id: 'actions', numeric: true, disablePadding: false, label: 'Ações' },
  ];

  const createData = (p: any) => ({
    id: p.id || `${p.numcot}-${p.numsol}-${p.cotacao?.apefor || p.apefor || ''}-${p.cotacao?.cplite || p.cplite || ''}`,
    numero: p.numcot,
    numerosolicitacao: p.numsol || '',
    fornecedor: p.fornecedor || p.cotacao?.apefor || p.apefor || '',
    produto: p.produto || p.cotacao?.cplite || p.cplite || '',
    quantidade: p.quantidade !== '' ? formatNumber(p.quantidade) : '',
    precounitario: p.precounitario !== '' ? formatCurrency(p.precounitario) : '',
    valorliquido: p.valorliquido !== '' ? formatCurrency(p.valorliquido) : '',
    nomemp: p.cotacao?.nomemp || p.nomemp || '',
    datcot: p.cotacao?.datcot || p.datcot || '',
    ...p
  });

  const tableData = selectedData.map((quote) => {
    const quoteData = quote?.cotacao ?? quote;
    const solicitationData = quote?.solicitacao ?? quote;
    const unitPrice = readFirstValue(quoteData, ['precot', 'valorunitario', 'vlruni', 'precounitario']);
    const quantity = readFirstValue(solicitationData, ['qtdsol', 'qtd', 'quantidade']);
    const unitPriceValue = normalizeToNumber(unitPrice) ?? 0;
    const quantityValue = normalizeToNumber(quantity) ?? 0;
    const netValue = unitPriceValue * quantityValue;

    return createData({
      ...quote,
      fornecedor: quoteData?.apefor || quote?.apefor || '',
      produto: quoteData?.cplite || quote?.cplite || '',
      quantidade: quantity,
      precounitario: unitPriceValue,
      valorliquido: netValue,
    });
  });

  const buildApprovalPayload = (items: any[]) => {
    return items.map((item) => {
      const quoteData = item?.cotacao ?? item;
      const solicitationData = item?.solicitacao ?? item;
      const unitPrice = readFirstValue(quoteData, ['precot', 'valorunitario', 'vlruni', 'precounitario']);

      const sameQuoteItems = items.filter((candidate) => {
        const candidateSolicitation = candidate?.solicitacao ?? candidate;
        const candidateQuote = candidate?.cotacao ?? candidate;

        return `${candidateSolicitation?.numsol ?? candidate?.numsol ?? ''}` === `${solicitationData?.numsol ?? item?.numsol ?? ''}`
          && `${candidateQuote?.cplite ?? candidate?.cplite ?? ''}` === `${quoteData?.cplite ?? item?.cplite ?? ''}`;
      });

      const lowestUnitPrice = Math.min(
        ...sameQuoteItems.map((candidate) => {
          const candidateQuote = candidate?.cotacao ?? candidate;
          return toComparableNumber(readFirstValue(candidateQuote, ['precot', 'valorunitario', 'vlruni', 'precounitario']));
        })
      );

      return {
        ...item.data,
        melcot: String(toComparableNumber(unitPrice) === lowestUnitPrice ? 1 : 0),
      };
    });
  };

  const { isFetching, refetch } = useQuery(['approveQuotesEmit'], async () => {
    const token = localStorage.getItem('token');
    if (token) {
      const res = await getErp({ route: 'cotacoes', idSystem: 'PORTAL', pagina: 0, quantidade: 20, numitem: 1000 }, token);
      if (res) {
        setSelectedData(res.map((r: any, idx: number) => ({
          ...r,
          id: `${r.numcot ?? ''}-${r.numsol ?? ''}-${r.cotacao?.apefor ?? r.apefor ?? ''}-${r.cotacao?.cplite ?? r.cplite ?? ''}-${idx}`,
          data: r,
          item: [{ numcot: r.numcot }],
        })));
        setCount(res.length || 0);
      }
    }
    return [];
  }, { refetchOnWindowFocus: false });

  const handleSetSelected = (value: SetStateAction<string[]>) => {
    setSelected((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;

      if (next.length <= 1) {
        return next;
      }

      return [next[next.length - 1]];
    });
  };

  const handleOpenDrawer = (id?: string) => {
    if (id) {
      const item = selectedData.find(d => `${d.id}` === id);
      if (item) {
        setSelected([`${item.id}`]);
        setDrawerRecord(item);
      }
    }
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelected([]);
    setDrawerRecord(null);
  };

  const Alert = (props: AlertProps) => {
    return <MuiAlert elevation={6} variant="filled" {...props} />;
  }

  const handleApprove = async (id?: string) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No token');

      const toApprove = selected.length > 0 ? selected : (id ? [id] : []);
      const payload = buildApprovalPayload(selectedData.filter(d => toApprove.find(s => `${s}` === `${d.id}`)));

      const res = await approveQuotes(payload, token);
      setIsLoading(false);
      if (res && res.statusCode === 200) {
        setIsSnackbar({ open: true, message: 'Cotações aprovadas com sucesso', title: 'success' });
        refetch();
        setSelected([]);
      } else {
        setIsSnackbar({ open: true, message: res?.message || 'Erro ao aprovar', title: 'error' });
      }
    } catch (err) {
      setIsLoading(false);
      setIsSnackbar({ open: true, message: 'Erro ao aprovar cotações', title: 'error' });
    }
  };

  return (
    <div className={classes.root}>
      <div style={{ padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Aprovar Cotações</Typography>
      </div>
      <div className={classes.tableSection}>
        <Container className={classes.tableSectionContainer}>
          <EnhancedTable
            title="Cotações"
            rowsPerPageOptions={[]}
            isClickable
            data={tableData}
            count={count}
            orderBy={'numero'}
            setOrderBy={() => {}}
            headCells={headCells}
            isLoading={isFetching}
            selected={selected}
            setSelected={handleSetSelected}
            page={1}
            setPage={() => {}}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            handleInfo={(id) => {
              handleOpenDrawer(id);
            }}
            handleApproved={(id) => {
              handleApprove(id);
            }}
            buttonsSelected={[
              selected.length === 1 && (
                <Tooltip title="Aprovar" key="approved"><IconButton onClick={() => handleApprove(`${selected[0]}`)}><DoneIcon style={{ color: '#fff' }} /></IconButton></Tooltip>
              ),
              (selected.length === 1) && (
                <Tooltip title="Info" key="info"><IconButton onClick={() => handleOpenDrawer(`${selected[0]}`)}><Info style={{ color: '#fff' }} /></IconButton></Tooltip>
              )
            ]}
            buttonsActions={['approved', 'info']}
          />
        </Container>
      </div>

      <ApproveQuotesEmitDrawer
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        record={drawerRecord}
        onApprove={() => handleApprove()}
        isApproving={isLoading}
      />
      <Snackbar open={isSnackbar.open} autoHideDuration={6000} onClose={() => setIsSnackbar({ ...isSnackbar, open: false })}>
        <Alert onClose={() => setIsSnackbar({ ...isSnackbar, open: false })} severity={isSnackbar.title as any}>
          {isSnackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ApproveQuotesEmit;
